/**
 * CopyConceptModal — duplicate a Project Concept into a new, independent concept.
 *
 * Opens via CustomEvent('open-copy-concept-modal', { detail: { conceptId, projectId } }) from
 * the concept's ⋯ menu ("Copy concept"). One name field (defaults to "<name> (Copy)") and an
 * optional "set as the selected concept" tick, then a single server call
 * (`services/design.js#copyConcept` → api/concept-copy.js) that copies the concept record, its
 * Units → Levels → Rooms, the AlderCAD model, and the Building Information Model plans, all
 * re-linked to the new rows so the two concepts can be edited independently from then on.
 *
 * On success the new record is pushed into ctx.conceptCache and both 'outline-render' and
 * 'concepts-updated' fire (the AddConceptModal recipe), then a short summary of what came
 * across is shown with a direct "Open AlderCAD" shortcut.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { copyConcept, setSelectedConcept, getConcepts, findConceptByName } from '../services/design.js'

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const helpStyle = {
  fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)',
  lineHeight: 1.45, marginTop: 5,
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 13,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}
const btnGhost = {
  padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none',
  border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)',
}

function conceptName(conceptId) {
  const list = Array.isArray(ctx.conceptCache) ? ctx.conceptCache : []
  const rec = list.find(c => c.id === conceptId)
  return (rec && rec.fields && rec.fields['Parti Pris Title']) || 'Concept'
}

export default function CopyConceptModal() {
  const [open, setOpen] = useState(false)
  const [conceptId, setConceptId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [sourceName, setSourceName] = useState('')
  const [name, setName] = useState('')
  const [concepts, setConcepts] = useState(null) // project's concepts, for the unique-name check; null while loading
  const [markSelected, setMarkSelected] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)   // { conceptId, counts } once copied

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const cid = e.detail?.conceptId || ''
      const src = conceptName(cid)
      setConceptId(cid)
      setProjectId(e.detail?.projectId || '')
      setSourceName(src)
      setName(src + ' (Copy)')
      setConcepts(null)
      setMarkSelected(false)
      setError(null)
      setResult(null)
      setSubmitting(false)
      setOpen(true)
      // Names are unique per project: load the list for the live check, and bump the default
      // past any "(Copy)" / "(Copy 2)" already taken.
      const pid = e.detail?.projectId || ''
      if (pid) {
        getConcepts(pid).then(list => {
          setConcepts(list || [])
          let candidate = src + ' (Copy)'
          for (let n = 2; findConceptByName(list, candidate); n++) candidate = src + ' (Copy ' + n + ')'
          setName(prev => (prev === src + ' (Copy)' ? candidate : prev))
        }).catch(() => setConcepts([]))
      } else {
        setConcepts([])
      }
    }
    window.addEventListener('open-copy-concept-modal', onOpen)
    return () => window.removeEventListener('open-copy-concept-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open && !submitting) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, submitting])

  async function handleSubmit() {
    if (!conceptId || !projectId || !name.trim() || submitting || dupe) return
    setSubmitting(true); setError(null)
    try {
      const { concept, counts } = await copyConcept(conceptId, projectId, name.trim())
      if (markSelected) await setSelectedConcept(projectId, concept.id)
      if (Array.isArray(ctx.conceptCache)) {
        const rec = { id: concept.id, fields: { ...concept.fields, 'Selected Concept': markSelected } }
        if (markSelected) ctx.conceptCache.forEach(c => { if (c.fields) c.fields['Selected Concept'] = false })
        ctx.conceptCache = [...ctx.conceptCache, rec]
      }
      window.dispatchEvent(new Event('outline-render'))
      window.dispatchEvent(new CustomEvent('concepts-updated', { detail: { projectId } }))
      setResult({ conceptId: concept.id, counts })
      setSubmitting(false)
    } catch (ex) {
      setError(ex.message || 'Failed to copy concept'); setSubmitting(false)
    }
  }

  if (!open) return null
  const dupe = findConceptByName(concepts || [], name)
  const canSubmit = !!name.trim() && !submitting && concepts != null && !dupe

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="CopyConceptModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 460, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            Copy Concept <span style={{ color: 'var(--aj-text-muted)', fontWeight: 400 }}>· {sourceName}</span>
          </span>
          <button onClick={() => { if (!submitting) setOpen(false) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        {result ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>
              <strong>{name.trim()}</strong> is ready as its own concept. Edits to either concept no longer touch the other.
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', lineHeight: 1.6 }}>
              <li>Units, levels and rooms: <span style={{ fontFamily: '"DM Mono",monospace' }}>{result.counts.units} / {result.counts.levels} / {result.counts.rooms}</span></li>
              <li>AlderCAD model: {result.counts.model ? 'copied, rooms and levels re-linked' : 'none to copy yet'}</li>
              <li>Building Information Model plans: <span style={{ fontFamily: '"DM Mono",monospace' }}>{result.counts.plans}</span> plan{result.counts.plans === 1 ? '' : 's'}, <span style={{ fontFamily: '"DM Mono",monospace' }}>{result.counts.polygons}</span> room shape{result.counts.polygons === 1 ? '' : 's'}, <span style={{ fontFamily: '"DM Mono",monospace' }}>{result.counts.views}</span> unit view{result.counts.views === 1 ? '' : 's'}</li>
            </ul>
            <div style={helpStyle}>
              Not copied, by design: specifications, schedules, presentation images and PDF inserts. Those follow whichever concept is marked selected.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              {result.counts.model && (
                <button
                  onClick={() => window.open('/modeler?conceptId=' + encodeURIComponent(result.conceptId) + '&projectId=' + encodeURIComponent(projectId), '_blank')}
                  style={btnGhost}
                >Open AlderCAD</button>
              )}
              <button onClick={() => setOpen(false)} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
            <div>
              <label style={labelStyle}>New concept name</label>
              <input style={inputStyle} value={name} autoFocus disabled={submitting} onChange={e => setName(e.target.value)} />
              {dupe && (
                <div style={{ ...helpStyle, color: '#c0392b' }}>
                  Another concept is already named "{dupe.fields['Parti Pris Title']}". Concept names must be unique within a project.
                </div>
              )}
              <div style={helpStyle}>
                Copies the concept's details, its units, levels and rooms, its AlderCAD model, and its Building
                Information Model plans into a new concept you can edit on its own. Specifications, schedules and
                presentation images stay with the original.
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={markSelected} disabled={submitting} onChange={e => setMarkSelected(e.target.checked)}
                style={{ marginTop: 2, accentColor: '#1D9E75', cursor: 'pointer' }} />
              <div>
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>Set the copy as the selected concept</div>
                <div style={helpStyle}>Leave off to keep the original active and work on the copy as a candidate.</div>
              </div>
            </label>

            {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button onClick={() => setOpen(false)} disabled={submitting} style={btnGhost}>Cancel</button>
              <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
                {submitting ? 'Copying…' : 'Copy Concept'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
